import React, { useState, useEffect } from 'react';
import {
    Button,
    Card,
    Col,
    Form,
    Input,
    Modal,
    Row,
    Space,
    Table,
    message,
    Popconfirm,
    Select,
    Layout,
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import axios from '../api/axios';
import Container from '../components/Container/Container';
import useAuthHeader from '../hooks/useAuthHeader';
import getBankWithdraw from '../api/withdraw-account/getBankWithdraw';

const { Content } = Layout;

const BlockedBanks = () => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
        total: 0
    });
    const [modalVisible, setModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [editingRecord, setEditingRecord] = useState(null);
    const [banks, setBanks] = useState([]);
    const user = useSelector((state) => state.auth.user);
    const accessToken = useAuthHeader();
    const [searchForm] = Form.useForm();
    const [searchParams, setSearchParams] = useState({});

    const fetchBanks = async () => {
        try {
            const response = await axios.get('blocked-banks/getListBank', {
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                }
            });
            if (response.data.success) {
                setBanks(response.data.data);
            }
        } catch (error) {
            message.error('Không thể tải danh sách ngân hàng');
        }
    };

    const fetchData = async (page = 1, params = searchParams) => {
        try {
            setLoading(true);
            const response = await axios.get(`blocked-banks/getBlockedBanks`, {
                params: {
                    page,
                    ...params
                },
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                }
            });
            if (response.data.success) {
                setData(response.data.data.data);
                setPagination({
                    current: response.data.data.currentPage,
                    pageSize: response.data.data.perPage,
                    total: response.data.data.total
                });
            }
        } catch (error) {
            message.error('Không thể tải danh sách ngân hàng bị chặn');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBanks();
        fetchData();
    }, []);

    const handleAdd = () => {
        form.resetFields();
        setEditingRecord(null);
        setModalVisible(true);
    };

    const handleEdit = (record) => {
        form.setFieldsValue(record);
        setEditingRecord(record);
        setModalVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            await axios.post(`blocked-banks/deleteBlockedBank`, {
                id: id
            }, {
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                },
            });
            message.success('Xóa thành công');
            fetchData();
        } catch (error) {
            message.error('Không thể xóa ngân hàng bị chặn');
        }
    };

    const handleSubmit = async (values) => {
        try {
            if (editingRecord) {
                await axios.post(`blocked-banks/updateBlockedBank`, values, {
                    headers: {
                        'Authorization': `Bearer ${accessToken}`
                    }
                });
                message.success('Cập nhật thành công');
            } else {
                await axios.post('blocked-banks/addBlockedBank', values, {
                    headers: {
                        'Authorization': `Bearer ${accessToken}`
                    }
                });
                message.success('Thêm mới thành công');
            }
            setModalVisible(false);
            fetchData();
        } catch (error) {
            message.error('Có lỗi xảy ra khi lưu dữ liệu');
        }
    };

    const handleTableChange = (newPagination) => {
        fetchData(newPagination.current);
    };

    const handleSearch = async (values) => {
        await fetchData(1, values);
        setSearchParams(values);
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    const handleReset = () => {
        searchForm.resetFields();
        const emptyParams = {};
        setSearchParams(emptyParams);
        fetchData(1, emptyParams);
    };

    const columns = [
        {
            title: 'Ngân hàng',
            dataIndex: 'bankCode',
            key: 'bankCode',
            width: '25%',
            render: (bankCode) => {
                const bank = banks.find(b => b.bankCode === bankCode);
                return bank ? (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span>{bank.bankName} - {bank.shortName}</span>
                    </div>
                ) : bankCode;
            }
        },
        {
            title: 'Số tài khoản',
            dataIndex: 'accountNumber',
            key: 'accountNumber',
            width: '15%',
            align: 'center',
            render: (text) => <span style={{ fontFamily: 'monospace' }}>{text}</span>
        },
        {
            title: 'Tên tài khoản',
            dataIndex: 'accountName',
            key: 'accountName',
            width: '20%',
            render: (text) => <span style={{ textTransform: 'uppercase' }}>{text}</span>
        },
        {
            title: 'Lý do',
            dataIndex: 'reason',
            key: 'reason',
            width: '20%',
        },
        {
            title: 'Trạng thái',
            dataIndex: 'isActive',
            key: 'isActive',
            width: '10%',
            align: 'center',
            render: (isActive) => (
                <span style={{
                    padding: '4px 8px',
                    borderRadius: '4px',
                    backgroundColor: isActive ? '#f6ffed' : '#fff1f0',
                    color: isActive ? '#52c41a' : '#ff4d4f',
                    border: `1px solid ${isActive ? '#b7eb8f' : '#ffa39e'}`
                }}>
                    {isActive ? 'Đang chặn' : 'Không chặn'}
                </span>
            ),
        },
        {
            title: 'Thao tác',
            key: 'action',
            width: '10%',
            align: 'center',
            render: (_, record) => (
                <Space size="small">
                    <Button
                        type="primary"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                    />
                    <Popconfirm
                        title="Bạn có chắc chắn muốn xóa?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Đồng ý"
                        cancelText="Hủy"
                    >
                        <Button
                            type="primary"
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                        />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <Container>
            <Content style={{ padding: '24px' }}>
                <Card title="Quản lý ngân hàng bị chặn">
                    <Form
                        form={searchForm}
                        onFinish={handleSearch}
                        style={{ marginBottom: 24 }}
                    >
                        <Row gutter={16}>
                            <Col xs={24} sm={12} md={6}>
                                <Form.Item name="bankCode" label="Ngân hàng">
                                    <Select
                                        allowClear
                                        placeholder="Chọn ngân hàng"
                                        showSearch
                                        optionFilterProp="children"
                                    >
                                        {banks.map(bank => (
                                            <Select.Option key={bank.bankCode} value={bank.bankCode}>
                                                {bank.bankName} - {bank.shortName}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                                <Form.Item name="accountNumber" label="Số tài khoản">
                                    <Input placeholder="Nhập số tài khoản" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                                <Form.Item name="accountName" label="Tên tài khoản">
                                    <Input placeholder="Nhập tên tài khoản" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                                <Form.Item name="isActive" label="Trạng thái">
                                    <Select allowClear placeholder="Chọn trạng thái">
                                        <Select.Option value={true}>Đang chặn</Select.Option>
                                        <Select.Option value={false}>Không chặn</Select.Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row>
                            <Col span={24} style={{ textAlign: 'right' }}>
                                <Space>
                                    <Button onClick={handleReset}>
                                        Đặt lại
                                    </Button>
                                    <Button type="primary" htmlType="submit">
                                        Tìm kiếm
                                    </Button>
                                    <Button
                                        type="primary"
                                        icon={<PlusOutlined />}
                                        onClick={handleAdd}
                                    >
                                        Thêm mới
                                    </Button>
                                </Space>
                            </Col>
                        </Row>
                    </Form>

                    <Table
                        columns={columns}
                        dataSource={data}
                        loading={loading}
                        rowKey="id"
                        pagination={pagination}
                        onChange={handleTableChange}
                        bordered
                        size="middle"
                        scroll={{ x: 1000 }}
                    />
                </Card>

                <Modal
                    title={editingRecord ? 'Sửa thông tin' : 'Thêm mới'}
                    open={modalVisible}
                    onCancel={() => setModalVisible(false)}
                    footer={null}
                >
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleSubmit}
                    >
                        <Form.Item
                            name="bankCode"
                            label="Ngân hàng"
                            rules={[{ required: true, message: 'Vui lòng chọn ngân hàng' }]}
                        >
                            <Select>
                                {banks.map(bank => (
                                    <Select.Option key={bank.bankCode} value={bank.bankCode}>
                                        {bank.bankName} - {bank.shortName}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Form.Item
                            name="accountNumber"
                            label="Số tài khoản"
                            rules={[{ required: true, message: 'Vui lòng nhập số tài khoản' }]}
                        >
                            <Input />
                        </Form.Item>
                        <Form.Item
                            name="accountName"
                            label="Tên tài khoản"
                            rules={[{ required: true, message: 'Vui lòng nhập tên tài khoản' }]}
                        >
                            <Input />
                        </Form.Item>
                        <Form.Item
                            name="reason"
                            label="Lý do"
                            rules={[{ required: true, message: 'Vui lòng nhập lý do' }]}
                        >
                            <Input.TextArea rows={4} />
                        </Form.Item>
                        <Form.Item
                            name="isActive"
                            label="Trạng thái"
                            rules={[{ required: true, message: 'Vui lòng chọn trạng thái' }]}
                        >
                            <Select>
                                <Select.Option value={1}>Đang chặn</Select.Option>
                                <Select.Option value={0}>Không chặn</Select.Option>
                            </Select>
                        </Form.Item>
                        <Form.Item>
                            <Space>
                                <Button type="primary" htmlType="submit">
                                    {editingRecord ? 'Cập nhật' : 'Thêm mới'}
                                </Button>
                                <Button onClick={() => setModalVisible(false)}>
                                    Hủy
                                </Button>
                            </Space>
                        </Form.Item>
                    </Form>
                </Modal>
            </Content>
        </Container>
    );
};

export default BlockedBanks; 
